/**
 * VerificationsScreen — Risk panel > Verifications > Reviews (Bill 2026-11-02).
 *
 * The LexisNexis companion of Sanctions > Reviews. A Moderate or High FraudPoint result holds the application: a
 * Moderate one until the follow-up documents are collected or a branch supervisor overrides it, a High one is a
 * decline. Every hold appears here automatically. Bill's rulings: Risk may clear or decline ANY band; the branch
 * supervisor override for Moderate stays.
 *   Clear   — Risk accepts the identity: the hold is released.
 *   Decline — do not open: the person stays blocked even if the follow-up is met or a supervisor overrode.
 * A reason is required; the officer who ran the LexisNexis check cannot decide it. The decision applies to that
 * LexisNexis report and prints on its Identity Verification report. Server: VerificationReviewService.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ExternalLink } from '@retail-ops/panel-shell';

import { fetchReportPdf } from '@/services/lexisNexisReportsApi';
import {
  decideVerification,
  loadVerificationReviews,
  type VerificationDecisionKind,
  type VerificationReviewItem,
} from '@/services/verificationReviewsApi';
import { formatLocal } from '@/utils/time';

const MIN_REASON = 10;

function OpenItem({ item, onDecided }: { item: VerificationReviewItem; onDecided: () => void }): JSX.Element {
  const [decision, setDecision] = useState<VerificationDecisionKind | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openReport = async (): Promise<void> => {
    if (!item.extractId) return;
    setError(null);
    try {
      const { url } = await fetchReportPdf('identity', item.extractId);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not print the report.');
    }
  };

  const submit = async (): Promise<void> => {
    if (!decision) return;
    setBusy(true);
    setError(null);
    try {
      await decideVerification(item, decision, reason.trim());
      onDecided();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The decision was not saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h2>
          {item.personName}{' '}
          <span className={item.band === 'High' ? 'pill pill--err' : 'pill pill--warn'}>
            FraudPoint {item.score} · {item.band}
          </span>
          {item.testApplication && <span className="pill pill--muted" style={{ marginLeft: 8 }}>Test application</span>}
        </h2>
        {item.extractId && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void openReport()}>
            <ExternalLink size={14} /> Identity Verification report
          </button>
        )}
      </div>
      <p className="form-hint">
        LexisNexis run {item.runAt ? formatLocal(item.runAt) : '—'}{item.runBy ? ` by ${item.runBy}` : ''} · branch {item.branch ?? '—'} ·
        officer {item.officerCode ?? '—'} · application {item.cartId.slice(0, 8)} · {item.market}
      </p>
      <div style={{ fontSize: 13 }}>
        {item.indexFlag && (
          <div className="banner banner--err" style={{ margin: '0 0 6px' }}>
            Fraud index above the review line: <strong>{item.indexFlag}</strong>
            {item.held ? ' — the application is held for your decision.' : ' — flagged for review; the opening is not stopped.'}
          </div>
        )}
        <div><strong>Still needed:</strong> {item.requirementText}</div>
        <div>Name/Address/SSN verification: {item.nameAddrSsn ?? '—'} · Date of birth match level: {item.dobMatchLevel ?? '—'} · Red flags: {item.redFlagCount}</div>
        {item.riskIndicators.length > 0 && (
          <div>Potential risk indicators: {item.riskIndicators.join('; ')}</div>
        )}
      </div>

      <fieldset style={{ border: 'none', padding: 0, margin: '12px 0 0' }}>
        <legend className="form-field__label">Decision</legend>
        <label style={{ display: 'block', marginBottom: 4 }}>
          <input type="radio" name={`v-${item.cartId}-${item.extractId ?? item.partyId}`} checked={decision === 'cleared'} onChange={() => setDecision('cleared')} />{' '}
          <strong>Clear</strong>: the identity is accepted. Releases the application.
        </label>
        <label style={{ display: 'block' }}>
          <input type="radio" name={`v-${item.cartId}-${item.extractId ?? item.partyId}`} checked={decision === 'declined'} onChange={() => setDecision('declined')} />{' '}
          <strong>Decline</strong>: do not open. The applicant stays blocked, even if the follow-up is completed later.
        </label>
      </fieldset>
      <label className="form-field">
        <span className="form-field__label">Reason (what you reviewed and why)</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={2000} />
      </label>
      {error && <div className="banner banner--err" role="alert">{error}</div>}
      <button
        type="button"
        className="btn btn--primary"
        disabled={busy || !decision || reason.trim().length < MIN_REASON}
        onClick={() => void submit()}
      >
        {busy ? 'Saving…' : 'Record decision'}
      </button>
    </section>
  );
}

export function VerificationsScreen(): JSX.Element {
  const [view, setView] = useState<'open' | 'decided'>('open');
  const [items, setItems] = useState<ReadonlyArray<VerificationReviewItem> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setItems(null);
    setError(null);
    try {
      setItems(await loadVerificationReviews(view));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the reviews.');
      setItems([]);
    }
  }, [view]);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <>
      <Link className="back-link" to="/">← Home</Link>
      <section className="page-header">
        <h1 className="page-header__title">Verifications</h1>
        <p className="page-header__subtitle">
          Identity verification (LexisNexis) results that are holding an application. A Moderate FraudPoint result holds
          it until the follow-up documents are collected or a branch supervisor overrides it; a High result is a decline;
          a fraud index above the review line (Risk settings) also comes here, whatever the score.
          Review the report and the applicant, then clear the result (the application is released) or decline it (do not
          open). Your decision and reason print on the Identity Verification report. Sanctions holds are under{' '}
          <Link to="/sanctions">Sanctions → Reviews</Link>.
        </p>
      </section>

      <nav className="subnav" role="tablist" aria-label="Verification views">
        <button type="button" role="tab" aria-selected>Reviews</button>
      </nav>

      <section className="card">
        <div className="action-row">
          <button type="button" className={view === 'open' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'} onClick={() => setView('open')}>
            Waiting for a decision
          </button>
          <button type="button" className={view === 'decided' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'} onClick={() => setView('decided')}>
            Recent decisions
          </button>
        </div>
      </section>

      {error && <section className="banner banner--err" role="alert">{error}</section>}
      {items === null && <p className="empty">Loading…</p>}
      {items !== null && items.length === 0 && !error && (
        <p className="empty">{view === 'open' ? 'No applications are waiting for a verification decision.' : 'No decisions yet.'}</p>
      )}

      {view === 'open' && items?.map((i) => (
        <OpenItem key={`${i.cartId}-${i.extractId ?? i.partyId}`} item={i} onDecided={() => void reload()} />
      ))}

      {view === 'decided' && items && items.length > 0 && (
        <section className="card">
          <table className="table">
            <thead>
              <tr><th>Decided</th><th>Name</th><th>FraudPoint</th><th>Decision</th><th>By</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.decision?.decisionId ?? `${i.cartId}-${i.extractId}`}>
                  <td>{i.decision ? formatLocal(i.decision.decidedAt) : '—'}</td>
                  <td>{i.personName}</td>
                  <td>{i.score} · {i.band}</td>
                  <td>
                    <span className={i.decision?.decision === 'cleared' ? 'pill pill--ok' : 'pill pill--err'}>
                      {i.decision?.decision === 'cleared' ? 'Cleared' : 'Declined'}
                    </span>
                  </td>
                  <td>{i.decision?.decidedBy ?? '—'}</td>
                  <td>{i.decision?.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
